import React from 'react';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';

import { ApplicationState } from 'store/store';
import { createDojoStyles } from './dojoStyles';
import { createTenantFontCssUrl } from './dojoFontCss';

function cleanUpContainer(containerId: string) {
    const myNode = document.getElementById(containerId);
    if (myNode) {
        myNode.innerHTML = '';
    }
}

type FormLoadState = 'loading' | 'success' | 'failed';

export const useDojoCard = (clientSessionSecret: string | undefined, containerId: string) => {
    const theme = useTheme();
    const { merchantId } = useSelector((state: ApplicationState) => state.settings);
    const [card, setCard] = React.useState<Dojo.Payment | null>(null);
    const [formLoadState, setFormLoadState] = React.useState<FormLoadState>('loading');
    const [formComplete, setFormComplete] = React.useState(false);

    const styles = React.useMemo(() => createDojoStyles(theme), [theme]);
    const fontCssUrl = React.useMemo(
        () => (merchantId ? createTenantFontCssUrl(merchantId) : undefined),
        [merchantId]
    );

    // Dojo fetches the stylesheet while the iframe loads and never reports back,
    // so the URL has to outlive the form — release it on unmount instead.
    React.useEffect(
        () => () => {
            if (fontCssUrl) {
                URL.revokeObjectURL(fontCssUrl);
            }
        },
        [fontCssUrl]
    );

    const onCardPaymentFormLoaded = React.useCallback(() => {
        setFormLoadState('success');
    }, []);

    const onCardPaymentFormLoadFailed = React.useCallback(() => {
        setFormLoadState('failed');
    }, []);

    const onFormComplete = React.useCallback(() => {
        setFormComplete(true);
    }, []);

    React.useEffect(() => {
        if (card || !clientSessionSecret) {
            return;
        }
        cleanUpContainer(containerId);
        setFormLoadState('loading');
        const config = {
            paymentDetails: {
                paymentToken: clientSessionSecret
            },
            containerId,
            // Dojo strips everything but the @font-face rules and injects them
            // into the iframe, which otherwise has no access to the app's fonts.
            fontCss: fontCssUrl ? [fontCssUrl] : undefined,
            styles,
            onIframeLoaded: onCardPaymentFormLoaded,
            onIframeLoadFailed: onCardPaymentFormLoadFailed,
            callbacks: {
                onFormComplete
            }
        };

        // intialising connection
        const dojoCard = new Dojo.Payment(config);

        setCard(dojoCard);
    }, [
        clientSessionSecret,
        containerId,
        onCardPaymentFormLoaded,
        onCardPaymentFormLoadFailed,
        onFormComplete,
        styles,
        fontCssUrl,
        card
    ]);

    return { card, formLoadState, formComplete };
};
